import type { AuthIdentity } from "@coinfallsmp/shared";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getMe } from "./api.js";

interface AuthContextValue {
  identity: AuthIdentity | null;
  loading: boolean;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  identity: null,
  loading: true,
  refresh: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    setLoading(true);
    getMe()
      .then(setIdentity)
      .catch(() => setIdentity(null))
      .finally(() => setLoading(false));
  }, [nonce]);

  return (
    <AuthContext.Provider value={{ identity, loading, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Convenience helpers derived from an AuthIdentity, shared across pages. */
export function creatorOf(identity: AuthIdentity | null) {
  return identity?.loggedIn ? identity.creator : null;
}

export function canUploadNow(identity: AuthIdentity | null): boolean {
  const creator = creatorOf(identity);
  if (!creator) return false;
  return creator.isAdmin || (creator.status === "approved" && creator.canUpload);
}
